import {
  Description,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { FC, ReactNode } from 'react';
import AlertIcon from '@/assets/alert.svg';
import { ModalLoader } from './modal-loader';

export type ModalProps = {
  buttonItems: ReactNode[];
  description?: string;
  loading?: boolean;
  open: boolean;
  title: string;
  setOpen: (open: boolean) => void;
  children?: ReactNode;
  large?: boolean;
};

/**
 * Generic modal component
 *
 * "Large" prop allows for the modal to expand to fit content
 *
 * TODO: - Add support for custom icons
 *         For this: identify uses? Make Alert the default? Enum for icons? Alert Info?
 *         Enum on types? alert | info?
 **/
export const Modal: FC<ModalProps> = ({
  buttonItems,
  description,
  loading,
  open,
  title,
  setOpen,
  children,
  large = false,
}) => (
  <Dialog className="relative z-50" onClose={setOpen} open={open}>
    <DialogBackdrop className="fixed inset-0 bg-gray-500/75 data-closed:opacity-0" />

    <div className="fixed inset-0 flex w-screen items-center justify-center">
      <DialogPanel
        className={`relative overflow-hidden rounded-lg bg-white shadow-xl ${large ? 'flex h-full w-full flex-col' : 'max-w-2xl'}`}
      >
        <div className={`${large ? 'flex min-h-0 flex-1 flex-col' : ''}`}>
          <div className="flex shrink-0 flex-row gap-4 p-6 pb-4">
            <div>
              <div className="bg-warningTint flex items-center justify-center rounded-full p-2 text-red-600">
                <AlertIcon />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <DialogTitle className="text-base font-semibold">
                {title}
              </DialogTitle>
              {description && (
                <Description className="text-sm text-gray-500">
                  {description}
                </Description>
              )}
            </div>
          </div>
          <div className={`${large ? 'flex-1 overflow-y-auto px-6' : ''}`}>
            {children}
          </div>
        </div>
        <div className="flex shrink-0 flex-row justify-end gap-3 rounded-b-lg bg-gray-50 px-4 py-3">
          {buttonItems}
        </div>
        {loading && <ModalLoader />}
      </DialogPanel>
    </div>
  </Dialog>
);
